import type { LayoutNode } from './types';
import { assertLayoutNodeExhaustive } from './layoutAssertNever';

/**
 * Structural child lists for a layout node (full tree — both `conditional` branches).
 * Leaf nodes return `[]`. Callers recurse into each inner array.
 */
export function getLayoutStructureChildLists(node: LayoutNode): LayoutNode[][] {
  switch (node.type) {
    case 'card':
    case 'stack':
    case 'grid':
    case 'flex':
    case 'box':
    case 'indent':
    case 'disabled-when':
    case 'repeat':
      return [node.children];
    case 'field-row':
      return [node.label, node.controls];
    case 'conditional': {
      const lists: LayoutNode[][] = [node.children];
      if (node.elseChildren) lists.push(node.elseChildren);
      return lists;
    }
    case 'table':
      // TODO(EMR-167): each cell is modeled as its own one-item list, so
      // cell-to-cell adjacency within a row is invisible to callers that
      // walk sibling lists for insertion/anchoring (e.g. section-port splice
      // in sectionLayoutPortSplice.ts) -- they can only see "before/after
      // the whole table", not "before/after this specific cell". This is a
      // known gap, not fixed: it only matters if a table is ever partially
      // shared between source and destination (some cells shared, some
      // source/dest-only). Today, bespoke tables are shared as a whole unit
      // between forms (all-or-none), so the gap has no live consequence. If
      // that assumption ever changes, model `row.cells` as one real sibling
      // list here (and in the `mapLayoutStructureChildren` mirror below)
      // instead of one list per cell.
      return node.rows.flatMap(row => row.cells.map(cell => [cell]));
    case 'OASISMultiselect':
      return node.children ? [node.children] : [];
    case 'divider':
    case 'static':
    case 'text':
    case 'display':
    case 'section-label':
    case 'sum':
    case 'OASISRadio':
    case 'OASISYesNoCheckbox':
    case 'OASISSelect':
    case 'OASISText':
    case 'OASISDate':
    case 'OASISTime':
    case 'OASISNumber':
    case 'OASISMultiselectOption':
    case 'radio-option':
      return [];
    default:
      return assertLayoutNodeExhaustive(node);
  }
}

export function walkLayoutStructureChildren(
  node: LayoutNode,
  walk: (nodes: LayoutNode[]) => void
): void {
  for (const list of getLayoutStructureChildLists(node)) {
    walk(list);
  }
}

/**
 * Return a shallow copy of `node` with every structural child list rebuilt by
 * `mapList` (write-side mirror of {@link getLayoutStructureChildLists}). Leaf
 * nodes are returned unchanged. Used by {@link spliceSectionPortLayout} when
 * pruning source-only subtrees and upserting donor blocks into the destination.
 */
export function mapLayoutStructureChildren(
  node: LayoutNode,
  mapList: (nodes: LayoutNode[]) => LayoutNode[]
): LayoutNode {
  switch (node.type) {
    case 'card':
    case 'stack':
    case 'grid':
    case 'flex':
    case 'box':
    case 'indent':
    case 'disabled-when':
    case 'repeat':
      return { ...node, children: mapList(node.children) };
    case 'field-row':
      return {
        ...node,
        label: mapList(node.label),
        controls: mapList(node.controls),
      };
    case 'conditional':
      return {
        ...node,
        children: mapList(node.children),
        ...(node.elseChildren
          ? { elseChildren: mapList(node.elseChildren) }
          : {}),
      };
    case 'table':
      // See the read-side TODO(EMR-167) above: each cell is mapped as its
      // own one-item list, so a `mapList` that drops a cell (e.g. pruning a
      // fully source-only cell) silently shrinks `cells` by one and shifts
      // every later cell's column alignment. Only reachable on a partial
      // table share, which doesn't happen today (tables are all-or-none).
      return {
        ...node,
        rows: node.rows.map(row => ({
          ...row,
          cells: row.cells.flatMap(cell => mapList([cell])),
        })),
      };
    case 'OASISMultiselect':
      return node.children
        ? { ...node, children: mapList(node.children) }
        : node;
    case 'divider':
    case 'static':
    case 'text':
    case 'display':
    case 'section-label':
    case 'sum':
    case 'OASISRadio':
    case 'OASISYesNoCheckbox':
    case 'OASISSelect':
    case 'OASISText':
    case 'OASISDate':
    case 'OASISTime':
    case 'OASISNumber':
    case 'OASISMultiselectOption':
    case 'radio-option':
      return node;
    default:
      return assertLayoutNodeExhaustive(node);
  }
}
